import * as amplitude from '@amplitude/analytics-browser';
import shouldDisableTracking from '@calm/web-common/dist/shouldDisableTracking';
import { Environment } from '@rhg/constants';
import { getUserBrowser, getUserOs } from '@rhg/utils/browser';
import { GenericAnalyticsDestination } from '../types';

export async function enrichAmplitudeEvent(event: amplitude.Types.Event) {
    const browser = getUserBrowser();
    const os = getUserOs();

    event.user_properties = {
        ...event.user_properties,
        $set: {
            ...event.user_properties?.$set,
            BrowserType: browser.name,
            BrowserVersion: browser.version,
        },
    };

    event.os_name = os.name;
    event.os_version = os.version;
    return event;
}

/**
 * Setup the amplitude plugin to track browser and os information
 */
function amplitudeSetup() {
    const plugin: amplitude.Types.Plugin = {
        name: 'track-browser-os',
        type: 'enrichment',
        execute: enrichAmplitudeEvent,
    };
    amplitude.add(plugin);
}

type AmplitudeDestination = GenericAnalyticsDestination<
    'amplitude',
    amplitude.Types.BrowserClient
>;

export function createAmplitudeDestination(
    token: string,
    env: Environment,
    baseUrl: string
): AmplitudeDestination {
    amplitudeSetup();
    return {
        name: 'amplitude',
        // We are not using separate amplitude instances so don't use createInstance()
        init: () =>
            amplitude.init(token, {
                serverUrl: baseUrl,
                // We have a custom debug destination to log to the console
                logLevel: amplitude.Types.LogLevel.None,
                // Change to shouldDisableTracking(false) to enable tracking in local
                optOut: shouldDisableTracking(env === Environment.LOCAL),
                trackingOptions: {
                    ipAddress: false,
                    language: true,
                    platform: true,
                },
                defaultTracking: {
                    sessions: true,
                    pageViews: false, // Done elsewhere
                },
            }).promise,
        track: async (event, properties) => {
            await amplitude.track(event, properties).promise;
        },
        flush: () => amplitude.flush().promise,
        reset: () => {
            amplitude.reset();
        },
        identify: async (uuid: string) => {
            const id = new amplitude.Identify();
            await amplitude.identify(id, { user_id: uuid }).promise;
        },
        setUserProperties: async (props) => {
            const id = new amplitude.Identify();
            Object.entries(props).forEach(([key, value]) => {
                id.set(key, value);
            });
            await amplitude.identify(id).promise;
        },
        register: async (props) => {
            const id = new amplitude.Identify();
            Object.entries(props).forEach(([key, value]) => {
                id.set(key, value);
            });
            await amplitude.identify(id).promise;
        },
        isLoaded: () => true,
        raw: amplitude,
    };
}
